<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { getSwipeUtils } from '@/components/lib/GwUtils/utils_swipe'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
// import GwTransition from '@/components/lib/GwTransition/GwTransition.vue'
import GwBox from '../GwBox/GwBox.vue'
import type { BoxVariants } from '../GwBox/gwBoxVariants'

export interface GwMenuBoxProps {
  variant: BoxVariants
  transitionVariant:
    | 'fade' //
    | 'slidebottom_menu'
    | 'slideleft'
    | 'slideright'
    | 'dialog'
  hasPullDown?: boolean
  hasBackOverlay?: boolean
  hasBackOverlayBlur?: boolean
  hasClose?: boolean
  tabindex?: number
}

withDefaults(defineProps<GwMenuBoxProps>(), {
  tabindex: 1,
})

const emit = defineEmits(['close'])

const pullY = ref(0)
const isPulling = ref(false)
const hasPullReleased = ref(false)

const menuRef = ref<HTMLElement | null>(null)

onMounted(() => {
  if (menuRef.value) {
    menuRef.value.focus()
  }
})

const swpEvents = getSwipeUtils({
  onTouchstart: e => {
    if (document.body.classList.contains('gw_dragging')) {
      hasPullReleased.value = true
      return
    }
    hasPullReleased.value = false
    isPulling.value = false
  },
  onTouchmove: (e, _args, { swp_yDiff }) => {
    if (hasPullReleased.value) return
    if (swp_yDiff < 0) {
      isPulling.value = true
      pullY.value = swp_yDiff < 0 ? swp_yDiff : 0
    }
  },
  onTouchend: (e, _args, { swp_yDiff, swp_timedown }) => {
    if (hasPullReleased.value) return
    isPulling.value = false
    if (swp_yDiff < 0) {
      const fastSwipeDown = Date.now() - swp_timedown < 300 && swp_yDiff < -50
      const pullNearEnd = e.currentTarget.clientHeight + swp_yDiff < 100
      if (fastSwipeDown || pullNearEnd) {
        e.preventDefault()
        emit('close')
        return
      }
      pullY.value = 0
    }
    hasPullReleased.value = true
  },
})

const close = () => {
  emit('close')
}
</script>
<template>
  <div
    ref="menuRef"
    class="gwmenubox_container"
    :class="`gwmenubox_container_transitionVariant_${transitionVariant}`"
    :tabindex
    @keydown.stop.prevent.esc="close"
  >
    <div
      v-if="hasPullDown || hasBackOverlay"
      class="gwmenubox_backoverlay gwtr_FadeIn_item"
      :class="hasBackOverlayBlur && 'gwmenubox_backoverlay_blur'"
      @click="close"
    />

    <div
      class="gwmenubox_content gwtr_ScaleFadeIn_item"
      :style="hasPullDown ? `bottom: ${pullY}px` : ''"
      :class="[
        (hasPullDown || hasBackOverlay) && 'gwmenubox_over_backoverlay',
        isPulling && `gwmenubox_pulling gwmenubox_pulling`,
        hasPullReleased && `gwmenubox_pullrelease`,
      ]"
      v-on="
        hasPullDown
          ? {
              ...swpEvents,
              mousedown: swpEvents.touchstart,
              mousemove: swpEvents.touchmove,
              mouseup: swpEvents.touchend,
            }
          : {}
      "
    >
      <GwBox
        :variant
        class="gwmenubox_pullcontent flex-1 mh-0 mw-0 d-flex"
        :class="hasClose && hasPullDown ? 'gwmenubox_pulldownclosepadding' : ''"
      >
        <slot />
        <div
          v-if="hasClose && hasPullDown"
          class="gwmenubox_pulldownclose"
        />
        <GwBtn
          v-else-if="hasClose"
          :variant="transitionVariant === 'dialog' ? 'btn_text_white' : 'btn_text_grey_white'"
          icon="mdi-close"
          class="gwmenubox_close"
          data-tst="gwmenu_box_close"
          @click="close"
        ></GwBtn>
      </GwBox>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// to tackle dragging scollable content down then up and then pulling down
// - pull starts - and then scrollable content is not released properly - prevented with overflow hidden
// body:not(.gw_dragging) {
//   .gwmenubox_pulling {
//     .gwmenubox_pullcontent {
//       overflow: hidden;
//       & * {
//         overflow: hidden !important;
//       }
//     }
//   }
// }
.gwmenubox_container {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  max-height: var(--vh-100);
  max-width: 100vw;
  z-index: 20;
  --gwmenubox-close-inset: 0.25rem 0.25rem auto auto;
  &_transitionVariant {
    &_dialog {
      align-items: center;
      justify-content: center;
      height: 100vh;
      width: 100vw;
      --gwmenubox-close-inset: 0 -2.5rem auto auto;
    }
    &_slidebottom_menu {
      width: 100vw;
      width: 100vw;
    }
    &_slideright {
      min-height: var(--vh-3);
      max-height: var(--vh-3);
      .gwmenubox_content {
        height: 100%;
      }
    }
    &_slideleft {
      min-height: var(--vh-3);
      max-height: var(--vh-3);
      .gwmenubox_content {
        height: 100%;
      }
    }
  }
}
.gwmenubox {
  &_pullrelease {
    transition: bottom 0.22s;
  }
  &_content {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
  }
  &_backoverlay {
    position: absolute;
    top: -500vw;
    left: -500vh;
    height: 10000vh;
    width: 10000vw;
    background: rgba(0, 0, 0, 0.5);
    z-index: 24;
    &_blur {
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
    }
  }
  &_over_backoverlay {
    z-index: 25;
  }
  &_pulldownclose {
    position: absolute;
    inset: 0.5rem -0.25rem auto auto;
    left: 50%;
    width: 33%;
    height: 0.5rem;
    background: var(--app-light-3);
    border-radius: 2px;
    transform: translateX(-50%);
  }
  &_pulldownclosepadding {
    padding-top: 1.25rem !important;
  }
  &_close {
    position: absolute;
    inset: var(--gwmenubox-close-inset);
  }
}
</style>
